import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Star, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO } from "@/constants/testIds";

export default function Hero({ onOpenLead, onOpenBookCall }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'url("https://images.pexels.com/photos/16707924/pexels-photo-16707924.jpeg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/40 to-[#050505]" />

      {/* Glow blob */}
      <div className="absolute top-1/3 -left-32 w-[480px] h-[480px] rounded-full bg-[#CCFF00]/20 blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#CCFF00]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="md:col-span-8"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] text-xs font-mono-jb uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Premium Web Design Studio
          </span>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            Websites that <span className="text-gradient-neon">convert</span>
            <br />
            visitors into <span className="italic font-light">customers.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed">
            High-end, lead-optimized websites with bright, interactive design.
            From hosting setup to full e-commerce — built fast, ranked well,
            and crafted to bring you measurable revenue.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              data-testid={HERO.ctaPrimary}
              onClick={() => scrollTo("pricing")}
              className="btn-neon rounded-none px-8 py-6 text-base h-auto"
            >
              Start Your Website Today
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              data-testid={HERO.ctaSecondary}
              onClick={onOpenBookCall}
              variant="outline"
              className="btn-outline-neon rounded-none px-8 py-6 text-base h-auto"
            >
              <CalendarDays className="mr-2 w-4 h-4" />
              Get a Personal Quote
            </Button>
          </div>

          {/* Social proof */}
          <div className="mt-12 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-[#CCFF00] text-[#CCFF00]"
                />
              ))}
              <span className="ml-2 text-sm text-zinc-300 font-medium">
                5.0 client rating
              </span>
            </div>
            <div className="text-sm text-zinc-400">
              <span className="text-white font-bold">120+</span> sites launched
            </div>
            <div className="text-sm text-zinc-400">
              <span className="text-white font-bold">2-5 day</span> avg delivery
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="md:col-span-4 hidden md:block"
        >
          <div className="relative">
            <div className="bento-card p-6 rotate-3 hover:rotate-0 transition-transform">
              <div className="text-[10px] font-mono-jb text-[#CCFF00] uppercase tracking-widest">
                Live Project
              </div>
              <div className="mt-2 font-display text-2xl font-bold">
                bellso.shop
              </div>
              <div className="text-zinc-400 text-sm">
                E-Commerce Experience
              </div>
              <div className="mt-4 h-2 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full w-[92%] bg-[#CCFF00]" />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-zinc-400">
                <span>Lighthouse</span>
                <span className="text-[#CCFF00] font-bold">92</span>
              </div>
            </div>

            <div className="bento-card p-5 -rotate-2 mt-6 hover:rotate-0 transition-transform">
              <div className="text-[10px] font-mono-jb text-[#CCFF00] uppercase tracking-widest">
                Conversion Lift
              </div>
              <div className="mt-2 font-display text-3xl font-black">
                +247%
              </div>
              <div className="text-zinc-400 text-xs">
                avg after our redesign
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
